import { useEffect, useRef, useState } from "react";

/**
 * BuddyPeek — a Buddy pokes up over a section seam with a cheerful bounce,
 * then settles into a gentle idle float.
 *
 * Place between two sections (zero-height wrapper); the Buddy's center sits
 * right on the junction line so it looks like it's peeking over the edge
 * of the section below.
 *
 * Props:
 *   src       — image URL (public asset path)
 *   alt       — alt text (purely decorative; a11y hidden from AT)
 *   side      — "left" | "right" (which side of the viewport)
 *   offset    — horizontal distance from `side` (e.g. "8%", "40px")
 *   width     — Buddy width in px (default 110)
 *   rotate    — resting rotation in deg (default -8)
 *   delay     — ms to wait before the peek-in animation (stagger multiple)
 *
 * Entrance uses IntersectionObserver so the peek fires once, when the
 * junction scrolls into view. Respects `prefers-reduced-motion`.
 */
export default function BuddyPeek({
  src,
  alt = "",
  side = "left",
  offset = "6%",
  width = 110,
  rotate = -8,
  delay = 0,
  /** When true, puts a solid white circle behind the Buddy so a transparent
   *  PNG doesn't visually merge with the cream page (e.g. Queso Kid). */
  bg = false,
  "data-testid": testId,
}) {
  const ref = useRef(null);
  const [peeked, setPeeked] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Prefers-reduced-motion → show the Buddy instantly, no animation.
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced) {
      setPeeked(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPeeked(true);
          io.disconnect();
        }
      },
      { threshold: 0, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden
      className="relative h-0 z-30 pointer-events-none"
    >
      <div
        className="absolute"
        style={{
          [side]: offset,
          // Half the Buddy sits above the junction, half below.
          top: `-${Math.round(width / 2)}px`,
          width: `${width}px`,
          height: `${width}px`,
          transformOrigin: "bottom center",
          transform: peeked
            ? `translateY(0) rotate(${rotate}deg)`
            : `translateY(60%) rotate(${rotate}deg) scale(0.6)`,
          opacity: peeked ? 1 : 0,
          transition: `transform 0.95s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.5s ease`,
          transitionDelay: `${delay}ms`,
          filter: "drop-shadow(0 10px 20px rgba(28, 22, 40, 0.22))",
        }}
      >
        <img
          data-testid={testId}
          src={src}
          alt={alt}
          className={peeked ? "buddy-peek-float" : ""}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            // Optional white disc backdrop — prevents transparent-PNG Buddies
            // from blending into the cream page (eyes/mouth looking "shot out").
            ...(bg
              ? {
                  background: "#fff",
                  borderRadius: "9999px",
                  padding: `${Math.round(width * 0.05)}px`,
                  border: "4px solid #fff",
                  boxShadow: "0 0 0 2px rgba(28,22,40,0.04)",
                }
              : {}),
          }}
        />
      </div>

      {/* Gentle idle float applied after peek-in. Uses --f variable so each
          instance can bob at a slightly different amplitude without needing
          multiple CSS classes. */}
      <style>{`
        .buddy-peek-float {
          animation: buddy-peek-float 4.5s ease-in-out infinite;
        }
        @keyframes buddy-peek-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .buddy-peek-float { animation: none; }
        }
      `}</style>
    </div>
  );
}
